import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Edit from "../../Images/Edit.png";
import Delete from "../../Images/deleteButton-icon.png";
import { Get_LeadIndicatorFunnel, LeadFunnel } from "../../services/userAPI";
import { useMutation, useQuery } from "@apollo/client";
import { message } from "antd";
import isEqual from "lodash.isequal";
import { DownOutlined, UpOutlined } from "@ant-design/icons";

const RoleLeadIndicatorConfiguration = ({ onIndicatorsChange, roleId, accountId }) => {
    const [isLeadCollapsed, setIsLeadCollapsed] = useState(false);
    const [isAddingNewIndicator, setIsAddingNewIndicator] = useState(false);
    const [indicators, setIndicators] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);

    const [indicatorName, setIndicatorName] = useState("");
    const [dailyNorm, setDailyNorm] = useState("");
    const [indicatorTarget, setIndicatorTarget] = useState("");
    const [color, setColor] = useState("#add8e6");
    const [errors, setErrors] = useState({});

    const location = useLocation();
    const finalRoleId = roleId || location.state?.roleId;
    const finalAccountId = accountId || location.state?.accountId;

    // Fetch existing lead indicators
    const { data, refetch } = useQuery(Get_LeadIndicatorFunnel, {
        variables: { roleId: finalRoleId },
        skip: !finalRoleId,
        fetchPolicy: "network-only",
    });

    const formatColorForDisplay = (colorValue) => {
        if (!colorValue) return "#add8e6";
        let colorCode = colorValue;
        if (colorCode.toLowerCase().startsWith("ff")) {
            colorCode = colorCode.slice(2);
        }
        if (!colorCode.startsWith("#")) {
            colorCode = `#${colorCode}`;
        }
        return colorCode;
    };

    const formatColorForAPI = (colorValue) => {
        if (!colorValue) return "add8e6";
        return colorValue.startsWith("#") ? colorValue.slice(1) : colorValue;
    };

    useEffect(() => {
        if (data?.getKiisForRole) {
            // Sort indicators by id in ascending order to show newest at the end
            const updatedIndicators = data.getKiisForRole
                .map(kpi => ({
                    id: kpi.id,
                    name: kpi.name,
                    dailyNorm: kpi.dailyTarget,
                    target: kpi.defaultValue,
                    color: formatColorForDisplay(kpi.colorValue),
                }))
                .sort((a, b) => a.id - b.id); // Sort by id ascending

            if (!isEqual(updatedIndicators, indicators)) {
                setIndicators(updatedIndicators);
                if (onIndicatorsChange) {
                    onIndicatorsChange(updatedIndicators);
                }
            }
        }
    }, [data, onIndicatorsChange]);

    const validate = () => {
        let newErrors = {};
        if (!indicatorName.trim()) newErrors.indicatorName = "Lead Indicator Name is required.";
        if (!dailyNorm || isNaN(dailyNorm) || Number(dailyNorm) <= 0) {
            newErrors.dailyNorm = "Daily Norm must be a positive number.";
        }
        if (!indicatorTarget || isNaN(indicatorTarget) || Number(indicatorTarget) <= 0) {
            newErrors.indicatorTarget = "Monthly Target must be a positive number.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const [saveKiiMasterDetails, { loading }] = useMutation(LeadFunnel);

    const resetForm = () => {
        setIndicatorName("");
        setDailyNorm("");
        setIndicatorTarget("");
        setColor("#add8e6");
        setIsAddingNewIndicator(false);
        setEditingIndex(null);
    };

    const handleCancel = () => {
        resetForm();
    };

    const handleAddOrUpdate = async () => {
        if (validate()) {
            try {
                const isEditing = editingIndex !== null;
                const selectedIndicator = isEditing ? indicators[editingIndex] : null;

                const brandColor = formatColorForAPI(color);

                const response = await saveKiiMasterDetails({
                    variables: {
                        accountId: finalAccountId,
                        roleId: finalRoleId,
                        kiiId: isEditing ? selectedIndicator.id : null,
                        kiiName: indicatorName,
                        kiiDailyTarget: Number(dailyNorm),
                        kiiDefaultTarget: Number(indicatorTarget),
                        kiiColorCode: brandColor,
                    },
                });

                if (response.data.saveKiiMasterDetails.success) {
                    await refetch();
                    const newIndicator = {
                        id: response.data.saveKiiMasterDetails.data.id,
                        name: response.data.saveKiiMasterDetails.data.name,
                        dailyNorm: response.data.saveKiiMasterDetails.data.dailyTarget,
                        target: response.data.saveKiiMasterDetails.data.defaultValue,
                        color: formatColorForDisplay(response.data.saveKiiMasterDetails.data.colorValue),
                    };

                    let updatedIndicators;
                    if (isEditing) {
                        updatedIndicators = indicators.map((indicator, index) =>
                            index === editingIndex ? newIndicator : indicator
                        );
                    } else {
                        // Add new indicator to the end
                        updatedIndicators = [...indicators, newIndicator];
                    }

                    // Sort by id ascending to maintain consistent order
                    updatedIndicators = updatedIndicators.sort((a, b) => a.id - b.id);

                    setIndicators(updatedIndicators);
                    if (onIndicatorsChange) {
                        onIndicatorsChange(updatedIndicators);
                    }

                    resetForm();
                } else {
                    message.error(response.data.saveKiiMasterDetails.message || "Failed to save lead indicator.");
                }
            } catch (err) {
                console.error("API Error:", err);
            }
        }
    };

    const handleEdit = (index) => {
        const indicator = indicators[index];
        setIndicatorName(indicator.name);
        setDailyNorm(indicator.dailyNorm);
        setIndicatorTarget(indicator.target);
        setColor(indicator.color);
        setEditingIndex(index);
        setIsAddingNewIndicator(true);
    };

    const handleDelete = async (index) => {
        const indicatorToDelete = indicators[index];

        try {
            const colorCode = formatColorForAPI(indicatorToDelete.color);

            const response = await saveKiiMasterDetails({
                variables: {
                    accountId: finalAccountId,
                    roleId: finalRoleId,
                    kiiId: indicatorToDelete.id,
                    kiiName: indicatorToDelete.name,
                    kiiDailyTarget: indicatorToDelete.dailyNorm,
                    kiiDefaultTarget: indicatorToDelete.target,
                    kiiColorCode: colorCode,
                    status: 0,
                },
            });

            if (response.data.saveKiiMasterDetails.success) {
                await refetch();
                const updatedIndicators = indicators.filter((_, i) => i !== index);
                setIndicators(updatedIndicators.sort((a, b) => a.id - b.id));
                if (onIndicatorsChange) {
                    onIndicatorsChange(updatedIndicators);
                }
            } else {
                message.error(response.data.saveKiiMasterDetails.message || "Failed to delete lead indicator.");
            }
        } catch (err) {
            console.error("API Error:", err);
        }
    };

    const editForm = (isEditing) => (
        <div className="flex flex-col gap-2.5 rounded-[8px] border border-[#ddd] bg-white p-[15px]">
            <input
                type="text"
                placeholder="Lead Indicator Name"
                className="w-full rounded-[5px] border border-[#ccc] p-2 outline-none"
                value={indicatorName}
                onChange={(e) => setIndicatorName(e.target.value)}
            />
            {errors.indicatorName && <span className="block text-[11px] text-[#CC0000]">{errors.indicatorName}</span>}

            <div className="flex items-center justify-between gap-2">
                <div className="flex flex-col items-center gap-[5px]">
                    <label className="text-[14px]">Color</label>
                    <input
                        type="color"
                        className="rl-funnel-color-input h-[25px] w-[25px] cursor-pointer appearance-none overflow-hidden rounded-full border border-[#ccc] p-[2px]"
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                    />
                </div>
                <div className="flex flex-col items-center gap-[5px]">
                    <label className="text-[14px]">Daily Norm</label>
                    <input
                        type="number"
                        min="0"
                        placeholder="000"
                        className="rl-funnel-num-input w-[100px] rounded-[5px] border border-[#ccc] p-2 text-center outline-none"
                        value={dailyNorm}
                        onChange={(e) => setDailyNorm(e.target.value)}
                    />
                    {errors.dailyNorm && <span className="block text-[11px] text-[#CC0000]">{errors.dailyNorm}</span>}
                </div>

                <div className="flex flex-col items-center gap-[5px]">
                    <label className="text-[14px]">Monthly Target</label>
                    <input
                        type="number"
                        min="0"
                        placeholder="000"
                        className="rl-funnel-num-input h-[40px] w-[100px] rounded-[8px] border border-[#ccc] p-2 text-center outline-none"
                        value={indicatorTarget}
                        onChange={(e) => setIndicatorTarget(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddOrUpdate();
                            }
                        }}
                    />
                    {errors.indicatorTarget && <span className="block text-[11px] text-[#CC0000]">{errors.indicatorTarget}</span>}
                </div>
            </div>

            <div className="mt-[10px] flex items-center justify-between gap-2.5">
                <button
                    className="flex h-[40px] items-center justify-center rounded-[5px] border border-[#c00] bg-white px-[15px] text-[14px] font-medium text-[#c00]"
                    onClick={handleCancel}
                >
                    Discard
                </button>
                <button
                    className={`flex h-[40px] items-center justify-center rounded-[5px] px-[15px] text-[14px] font-medium text-white ${
                        indicatorName && indicatorTarget ? "bg-[#c00] cursor-pointer" : "bg-[#ababab] text-[#e2e0e0] cursor-not-allowed"
                    }`}
                    onClick={handleAddOrUpdate}
                    disabled={!indicatorName || !indicatorTarget || loading}
                >
                    {loading ? <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></span> : (isEditing ? "Save" : "Add")}
                </button>
            </div>
        </div>
    );

    return (
        <div>
            <div className="flex flex-col gap-2.5">
                <div className="overflow-hidden rounded-[5px] border border-[#ddd] bg-white">
                    <button
                        className="flex w-full cursor-pointer items-center justify-between p-[15px] text-left text-[16px] font-medium"
                        onClick={() => setIsLeadCollapsed(!isLeadCollapsed)}
                    >
                        <span>Lead Indicator Configuration</span>
                        <span className="text-[12px] font-normal leading-none text-black">
                            {isLeadCollapsed ? <DownOutlined /> : <UpOutlined />}
                        </span>
                    </button>

                    {!isLeadCollapsed && (
                        <div className="border-t border-[#ddd] p-[15px]">
                            <div className="rl-funnel-scroll w-full overflow-x-auto overflow-y-hidden pb-2">
                                <div className="flex min-w-max flex-row gap-4">
                                    {indicators.map((indicator, index) =>
                                        editingIndex === index ? (
                                            <div key={indicator.id}>{editForm(true)}</div>
                                        ) : (
                                            <div
                                                key={indicator.id}
                                               className="flex h-[135px] w-[200px] shrink-0 flex-col rounded-[8px] border border-[#E0E0E0] bg-white p-[12px] shadow-[0_1px_4px_rgba(0,0,0,0.10)]"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span
                                                        title={indicator.name}
                                                        className="block max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap"
                                                    >
                                                        {indicator.name}
                                                    </span>
                                                    <div className="h-[20px] w-[20px] rounded-full" style={{ backgroundColor: indicator.color }}></div>
                                                </div>
                                                <div className="mt-[10px]">
                                                    <div className="flex justify-between text-[13px]">
                                                        <span>Daily Norm</span>
                                                        <strong>{indicator.dailyNorm}</strong>
                                                    </div>
                                                    <div className="flex justify-between text-[13px]">
                                                        <span>Monthly Target</span>
                                                        <strong>{indicator.target}</strong>
                                                    </div>
                                                </div>
                                                <div className="mt-auto flex items-center justify-between gap-2.5">
                                                    <img className="h-[28px] w-[28px] cursor-pointer" src={Edit} onClick={() => handleEdit(index)} alt="Edit" />
                                                    <img className="h-[16px] w-[15px] cursor-pointer" src={Delete} onClick={() => handleDelete(index)} alt="Delete" />
                                                </div>
                                            </div>
                                        )
                                    )}

                                    {isAddingNewIndicator && editingIndex === null && editForm(false)}

                                    {!isAddingNewIndicator && editingIndex === null && (
                                        <button
                                            className={
                                                indicators.length === 0
                                                    ? "h-[56px] w-[212px] mb-24 shrink-0 cursor-pointer self-center rounded-[4px] border border-[#AD0101] bg-[#AD0101] text-[16px] font-medium text-white"
                                                    : "h-[36px] min-w-[88px] mb-24 shrink-0 cursor-pointer self-center rounded-[4px] border border-[#AD0101] bg-[#AD0101] px-3 text-[16px] font-medium text-white"
                                            }
                                            onClick={() => setIsAddingNewIndicator(true)}
                                        >
                                            {indicators.length === 0 ? "New Lead Indicator" : "Add"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    <p className="ml-[2%] pb-3 text-[12px] text-[#888]">
                        The items displayed from left to right are arranged vertically from top to bottom in the app.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default RoleLeadIndicatorConfiguration;
